import { Avatar, AvatarFallback, AvatarImage } from '@ordo/ui/avatar'
import { Button } from '@ordo/ui/button'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@ordo/ui/card'
import { Input } from '@ordo/ui/input'
import { Label } from '@ordo/ui/label'
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@ordo/ui/select'
import { Page } from 'compositions/Page'
import { useUpdateMe } from 'hooks/api'
import { type ThemePreference, useTheme } from 'hooks/useTheme'
import { Save } from 'lucide-react'
import { useEffect, useState } from 'react'
import { useAuthStore } from 'stores/auth'

import { ApiKeysSection } from './api-keys-section'

function initials(name: string | null | undefined, email: string): string {
  const source = name?.trim() || email
  const parts = source.split(/\s+/).filter(Boolean).slice(0, 2)
  return parts.map((p) => p.charAt(0).toUpperCase()).join('') || '?'
}

function FormStatus({ error, savedAt }: { error: string | null; savedAt: number | null }) {
  if (error) {
    return <p className="text-sm text-destructive">{error}</p>
  }
  if (savedAt) {
    return <p className="text-sm text-muted-foreground">Saved.</p>
  }
  return <p className="text-sm text-muted-foreground" />
}

export function Settings() {
  const user = useAuthStore((s) => s.user)
  const { preference, setPreference } = useTheme(user)
  const { perform: updateMe, loading: saving } = useUpdateMe()

  const [name, setName] = useState(user?.name ?? '')
  const [picture, setPicture] = useState(user?.picture ?? '')
  const [error, setError] = useState<string | null>(null)
  const [savedAt, setSavedAt] = useState<number | null>(null)

  // Refresh local inputs when the user arrives (on mount) or rotates.
  useEffect(() => {
    if (!user) {
      return
    }
    setName(user.name ?? '')
    setPicture(user.picture ?? '')
  }, [user])

  if (!user) {
    return (
      <Page>
        <h1 className="mb-6 text-xl font-semibold">Settings</h1>
      </Page>
    )
  }

  const dirty = name !== (user.name ?? '') || picture !== (user.picture ?? '')

  async function handleSave(e: React.FormEvent) {
    e.preventDefault()
    setError(null)
    try {
      await updateMe({
        name: name.trim() || undefined,
        picture: picture.trim() === '' ? null : picture.trim(),
      })
      setSavedAt(Date.now())
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to save')
    }
  }

  return (
    <Page>
      <h1 className="mb-6 text-xl font-semibold">Settings</h1>
      <div className="space-y-6">
        <Card>
          <CardHeader>
            <CardTitle>Profile</CardTitle>
            <CardDescription>Your name and avatar. Shown across the app.</CardDescription>
          </CardHeader>
          <CardContent>
            <form onSubmit={handleSave} className="space-y-4">
              <div className="flex items-center gap-4">
                <Avatar className="h-14 w-14">
                  <AvatarImage src={picture || undefined} alt={name || user.email} />
                  <AvatarFallback>{initials(name, user.email)}</AvatarFallback>
                </Avatar>
                <div className="flex-1 space-y-2">
                  <Label htmlFor="settings-picture">Profile picture URL</Label>
                  <Input
                    id="settings-picture"
                    type="url"
                    inputMode="url"
                    placeholder="https://…"
                    value={picture}
                    onChange={(e) => setPicture(e.target.value)}
                  />
                </div>
              </div>
              <div className="space-y-2">
                <Label htmlFor="settings-name">Name</Label>
                <Input id="settings-name" value={name} onChange={(e) => setName(e.target.value)} />
              </div>
              <div className="space-y-2">
                <Label htmlFor="settings-email">Email</Label>
                <Input id="settings-email" value={user.email} readOnly disabled />
              </div>
              <div className="flex items-center justify-between">
                <FormStatus error={error} savedAt={savedAt} />
                <Button type="submit" disabled={!dirty || saving}>
                  <Save className="h-4 w-4" />
                  {saving ? 'Saving…' : 'Save'}
                </Button>
              </div>
            </form>
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle>Appearance</CardTitle>
            <CardDescription>Theme preference. System follows your OS setting.</CardDescription>
          </CardHeader>
          <CardContent>
            <div className="flex items-center gap-3">
              <Label htmlFor="settings-theme" className="text-sm">
                Theme
              </Label>
              <Select value={preference} onValueChange={(v) => setPreference(v as ThemePreference)}>
                <SelectTrigger id="settings-theme" className="h-9 w-40 text-sm">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="light">Light</SelectItem>
                  <SelectItem value="dark">Dark</SelectItem>
                  <SelectItem value="system">System</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </CardContent>
        </Card>

        <ApiKeysSection />
      </div>
    </Page>
  )
}
